import { Routes } from '@angular/router';
import { LoginPageComponent } from '@letly/ui-common';
import { provideEffects } from '@ngrx/effects';
import { provideState } from '@ngrx/store';
import { propertyDetailFeature } from './public-property-detail/data/property-detail.state';
import { PropertyDetailEffects } from './public-property-detail/data/property-detail.effects';
import { createPropertyFeature } from './landlord/create-property/data/create-property.reducers';
import { CreatePropertyEffect } from './landlord/create-property/data/create-property.effect';
import { CreatePropertyFacade } from './landlord/create-property/data/create-property-facade';
import { nativeAuthGuard, permissionGuard } from '@letly/services';
import { posthogEnabledGuard } from './core/services/posthog-enabled.guard';
import { unsavedChangesGuard } from './core/utils/unsaved-changes.guard';
import { Permission } from '@letly/models';
import { offeringFeature } from './offering/data/offering.state';
import { OfferingEffects } from './offering/data/offering.effects';
import { viewingFeature } from './viewings/data/viewing.state';
import { ViewingEffects } from './viewings/data/viewing.effects';
import { tenancyFeature } from './tenancy/data/tenancy.selectors';
import { TenancyEffects } from './tenancy/data/tenancy.effects';
import { accountFeature } from './account/data/account.reducer';
import { AccountEffects } from './account/data/account.effects';
import { landlordPropertyDetailFeature } from './landlord/landlord-property-detail/data/landlord-property-detail.state';
import { LandlordPropertyDetailEffects } from './landlord/landlord-property-detail/data/landlord-property-detail.effects';
import { contractsFeature } from './landlord/landlord-property-detail/contracts/data/contract.state';
import { ContractEffects } from './landlord/landlord-property-detail/contracts/data/contract.effects';
import { offerContractsFeature } from './offering/contracts/data/offer-contract.state';
import { OfferContractEffects } from './offering/contracts/data/offer-contract.effects';
import { walletFeature } from './landlord/wallet/data/wallet.selectors';
import { WalletEffects } from './landlord/wallet/data/wallet.effects';
import { notificationFeature } from './account/components/notifications/data/notification.reducer';
import { NotificationEffect } from './account/components/notifications/data/notification.effect';
import { letAgreedPropertyGuard } from './offering/guards/let-agreed-property.guard';

const offeringProviders = [
  provideState(offeringFeature),
  provideEffects(OfferingEffects),
  provideState(offerContractsFeature),
  provideEffects(OfferContractEffects),
];

const landlordPropertyDetailProviders = [
  provideState(landlordPropertyDetailFeature),
  provideEffects(LandlordPropertyDetailEffects),
  provideState(contractsFeature),
  provideEffects(ContractEffects),
];

export const appRoutes: Routes = [
  {
    path: '',
    redirectTo: 'list',
    pathMatch: 'full',
  },
  {
    path: 'login',
    component: LoginPageComponent,
    data: {
      showNavbar: { desktop: false, mobile: false },
      bottomNav: false,
      title: 'Sign in',
    },
  },
  {
    path: 'list',
    loadComponent: () =>
      import('./list-view/list-view.component').then(
        (m) => m.ListViewComponent,
      ),
    data: { showNavbar: { desktop: true }, role: 'tenant', reuse: true },
  },
  {
    path: 'cookie-settings',
    loadComponent: () =>
      import('./core/consent/cookie-settings.component').then(
        (m) => m.CookieSettingsComponent,
      ),
    data: {
      title: 'Cookie settings',
      showNavbar: { desktop: true, mobile: true },
      bottomNav: false,
    },
  },
  {
    path: 'references/:sessionId',
    loadComponent: () =>
      import('./references/public-reference-form.component').then(
        (m) => m.PublicReferenceFormComponent,
      ),
    data: {
      title: 'Reference',
      showNavbar: { desktop: true, mobile: false },
      showLinks: false,
      bottomNav: false,
    },
  },
  {
    path: 'invitations/:token',
    loadComponent: () =>
      import('./invitation/public-invitation.component').then(
        (m) => m.PublicInvitationComponent,
      ),
    data: {
      title: $localize`:@@invitation.public.page-title:Invitation`,
      showNavbar: { desktop: true, mobile: true },
      showLinks: false,
      showAuthActions: false,
      bottomNav: false,
    },
  },
  {
    path: 'invitations/:token/viewings/:viewingId',
    loadComponent: () =>
      import('./invitation/public-invitation.component').then(
        (m) => m.PublicInvitationComponent,
      ),
    data: {
      title: $localize`:@@invitation.public.page-title:Invitation`,
      showNavbar: { desktop: true, mobile: true },
      showLinks: false,
      showAuthActions: false,
      bottomNav: false,
    },
  },
  {
    path: 'property/:id',
    loadComponent: () =>
      import('./public-property-detail/public-property-detail.component').then(
        (m) => m.PublicPropertyDetailComponent,
      ),
    providers: [
      provideState(propertyDetailFeature),
      provideEffects(PropertyDetailEffects),
    ],
    data: {
      title: 'Property Detail',
      showNavbar: {
        desktop: true,
        mobile: false,
      },
      bottomNav: false,
    },
  },
  {
    path: 'property/:id/make-offer',
    loadComponent: () =>
      import('./offering/make-offer.component').then(
        (m) => m.MakeOfferComponent,
      ),
    canActivate: [nativeAuthGuard, letAgreedPropertyGuard],
    providers: [
      provideState(propertyDetailFeature),
      provideEffects(PropertyDetailEffects),
      provideState(offeringFeature),
      provideEffects(OfferingEffects),
    ],
    data: {
      isLandlord: false,
      bottomNav: false,
      title: $localize`:@@offering.make-offer.page-title:Make an offer`,
    },
  },
  {
    path: 'property/:id/offer-occupants',
    loadComponent: () =>
      import('./offering/offer-occupants/offer-occupants.component').then(
        (m) => m.OfferOccupantsComponent,
      ),
    canActivate: [nativeAuthGuard, letAgreedPropertyGuard],
    providers: [
      provideState(propertyDetailFeature),
      provideEffects(PropertyDetailEffects),
      ...offeringProviders,
    ],
    data: {
      isLandlord: false,
      bottomNav: false,
      title: $localize`:@@offering.occupants.page-title:Make an offer`,
    },
  },
  {
    path: 'property/:id/offer-requests',
    loadComponent: () =>
      import('./offering/offer-requests/offer-requests.component').then(
        (m) => m.OfferRequestsComponent,
      ),
    canActivate: [nativeAuthGuard, letAgreedPropertyGuard],
    providers: [
      provideState(propertyDetailFeature),
      provideEffects(PropertyDetailEffects),
      ...offeringProviders,
    ],
    data: {
      isLandlord: false,
      bottomNav: false,
      title: $localize`:@@offering.requests.page-title:Make an offer`,
    },
  },
  {
    path: 'property/:id/confirm-offer',
    loadComponent: () =>
      import('./offering/confirm-offer/confirm-offer').then(
        (m) => m.ConfirmOffer,
      ),
    canActivate: [nativeAuthGuard, letAgreedPropertyGuard],
    providers: [
      provideState(propertyDetailFeature),
      provideEffects(PropertyDetailEffects),
      ...offeringProviders,
    ],
    data: {
      isLandlord: false,
      bottomNav: false,
      title: $localize`:@@offering.review.page-title:Review offer`,
    },
  },
  {
    path: 'offers/:offerId',
    canActivate: [nativeAuthGuard],
    loadComponent: () =>
      import('./offering/offer-detail/offer-detail-tenant-container.component').then(
        (m) => m.OfferDetailTenantContainerComponent,
      ),
    providers: [...offeringProviders],
    data: {
      isLandlord$: false,
      navbar: true,
      bottomNav: false,
      title: 'Offer Details',
    },
  },
  {
    path: 'offers',
    canActivate: [nativeAuthGuard],
    loadComponent: () =>
      import('./offering/tenancy-offers-list/tenancy-offers-list.component').then(
        (m) => m.TenancyOffersListComponent,
      ),
    providers: [provideState(offeringFeature), provideEffects(OfferingEffects)],
    data: {
      showNavbar: { desktop: true, mobile: false },
      bottomNav: true,
      reuse: true,
    },
  },
  {
    path: 'tenancies',
    canActivate: [nativeAuthGuard],
    loadComponent: () =>
      import('./tenancy/tenancies/tenancies.component').then(
        (m) => m.TenanciesComponent,
      ),
    providers: [provideState(tenancyFeature), provideEffects(TenancyEffects)],
    data: {
      role: 'tenant',
      isLandlord: false,
      navbar: { desktop: true, mobile: false },
      bottomNav: true,
      reuse: true,
    },
  },
  {
    path: 'tenancies/:id',
    canActivate: [nativeAuthGuard],
    providers: [provideState(tenancyFeature), provideEffects(TenancyEffects)],
    children: [
      {
        path: '',
        loadComponent: () =>
          import('./tenancy/tenancy-detail/tenancy-detail-tenant-container.component').then(
            (m) => m.TenancyDetailTenantContainerComponent,
          ),
        data: { bottomNav: true, title: $localize`:@@tenancy.details.page-title:Tenancy details` },
      },
      {
        path: 'agreement',
        loadComponent: () =>
          import('./tenancy/tenancy-detail/tenancy-agreement/tenancy-agreement.component').then(
            (m) => m.TenancyAgreementComponent,
          ),
        data: { bottomNav: false, title: 'Tenancy Agreement' },
        providers: [...offeringProviders],
      },
    ],
    data: { role: 'tenant', isLandlord: false },
  },
  {
    path: 'viewings',
    canActivate: [nativeAuthGuard],
    loadComponent: () => import('./viewings/viewings').then((m) => m.Viewings),
    providers: [provideState(viewingFeature), provideEffects(ViewingEffects)],
    data: {
      bottomNav: true,
      reuse: true,
      showNavbar: { desktop: true, mobile: false },
    },
  },
  {
    path: 'viewings/:viewingId',
    canActivate: [nativeAuthGuard],
    loadComponent: () =>
      import('./viewings/viewing-detail/viewing-detail').then(
        (m) => m.ViewingDetail,
      ),
    providers: [provideState(viewingFeature), provideEffects(ViewingEffects)],
    data: {
      bottomNav: true,
      showNavbar: { desktop: true, mobile: false },
      title: $localize`:@@viewings.detail.page-title:Your viewing`,
    },
  },
  {
    path: 'landlord',
    canActivate: [nativeAuthGuard],
    data: { showNavbar: true, isLandlord: true, role: 'landlord' },
    children: [
      { path: '', redirectTo: 'listing', pathMatch: 'full' },
      {
        path: 'create-property/overview',
        loadComponent: () =>
          import('./landlord/create-property/components/create-property-overview/create-property-overview.component').then(
            (m) => m.CreatePropertyOverviewComponent,
          ),
        data: {
          showNavbar: { mobile: false, desktop: true },
          bottomNav: false,
        },
      },
      {
        path: 'channels',
        loadComponent: () =>
          import('./landlord/landlord-channels/landlord-channels.component').then(
            (m) => m.LandlordChannelsComponent,
          ),
        data: {
          showNavbar: { desktop: true, mobile: false },
          bottomNav: true,
        },
      },
      {
        path: 'create-property',
        canActivate: [permissionGuard],
        loadComponent: () =>
          import('./landlord/create-property/create-property.component').then(
            (m) => m.CreatePropertyComponent,
          ),
        providers: [
          provideState(createPropertyFeature),
          provideEffects(CreatePropertyEffect),
          CreatePropertyFacade,
        ],
        data: {
          showNavbar: { mobile: false, desktop: false },
          bottomNav: false,
          permission: Permission.LandlordPropertyCreate,
        },
        children: [
          {
            path: 'new',
            children: [
              {
                path: 'location',
                loadComponent: () =>
                  import('./landlord/create-property/components/create-property-address/create-property-address.component').then(
                    (m) => m.CreatePropertyAddressComponent,
                  ),
              },
              {
                path: 'ownership',
                loadComponent: () =>
                  import('./landlord/create-property/components/create-property-ownership/create-property-ownership').then(
                    (m) => m.CreatePropertyOwnership,
                  ),
              },
              {
                path: 'category',
                loadComponent: () =>
                  import('./landlord/create-property/components/create-property-category/create-property-category.component').then(
                    (m) => m.CreatePropertyCategoryComponent,
                  ),
              },
              {
                path: 'basic-info',
                loadComponent: () =>
                  import('./landlord/create-property/components/create-property-basic-information/create-property-basic-information.component').then(
                    (m) => m.CreatePropertyBasicInformationComponent,
                  ),
              },
              {
                path: 'availability',
                loadComponent: () =>
                  import('./landlord/create-property/components/create-property-availability/create-property-availability.component').then(
                    (m) => m.CreatePropertyAvailabilityComponent,
                  ),
              },
              {
                path: 'rent',
                loadComponent: () =>
                  import('./landlord/create-property/components/create-property-pricing/create-property-pricing.component').then(
                    (m) => m.CreatePropertyPricingComponent,
                  ),
              },
              {
                path: 'preferences',
                loadComponent: () =>
                  import('./landlord/create-property/components/create-property-preferences/create-property-preferences').then(
                    (m) => m.CreatePropertyPreferences,
                  ),
              },
              {
                path: 'tier',
                loadComponent: () =>
                  import('./landlord/create-property/components/create-property-tier/create-property-tier.component').then(
                    (m) => m.CreatePropertyTierComponent,
                  ),
              },
              {
                path: 'guaranteed',
                loadComponent: () =>
                  import('./landlord/create-property/components/create-property-guaranteed/create-property-guaranteed.component').then(
                    (m) => m.CreatePropertyGuaranteedComponent,
                  ),
              },
              {
                path: 'compliance',
                loadComponent: () =>
                  import('./landlord/create-property/components/create-property-compliance/create-property-compliance').then(
                    (m) => m.CreatePropertyCompliance,
                  ),
              },
              {
                path: 'agreement',
                loadComponent: () =>
                  import('./landlord/create-property/components/create-property-agreement/create-property-agreement').then(
                    (m) => m.CreatePropertyAgreement,
                  ),
              },
            ],
          },
          {
            path: ':id',
            children: [
              {
                path: 'location',
                loadComponent: () =>
                  import('./landlord/create-property/components/create-property-address/create-property-address.component').then(
                    (m) => m.CreatePropertyAddressComponent,
                  ),
              },
              {
                path: 'ownership',
                loadComponent: () =>
                  import('./landlord/create-property/components/create-property-ownership/create-property-ownership').then(
                    (m) => m.CreatePropertyOwnership,
                  ),
              },
              {
                path: 'category',
                loadComponent: () =>
                  import('./landlord/create-property/components/create-property-category/create-property-category.component').then(
                    (m) => m.CreatePropertyCategoryComponent,
                  ),
              },
              {
                path: 'basic-info',
                loadComponent: () =>
                  import('./landlord/create-property/components/create-property-basic-information/create-property-basic-information.component').then(
                    (m) => m.CreatePropertyBasicInformationComponent,
                  ),
              },
              {
                path: 'availability',
                loadComponent: () =>
                  import('./landlord/create-property/components/create-property-availability/create-property-availability.component').then(
                    (m) => m.CreatePropertyAvailabilityComponent,
                  ),
              },
              {
                path: 'rent',
                loadComponent: () =>
                  import('./landlord/create-property/components/create-property-pricing/create-property-pricing.component').then(
                    (m) => m.CreatePropertyPricingComponent,
                  ),
              },
              {
                path: 'preferences',
                loadComponent: () =>
                  import('./landlord/create-property/components/create-property-preferences/create-property-preferences').then(
                    (m) => m.CreatePropertyPreferences,
                  ),
              },
              {
                path: 'tier',
                loadComponent: () =>
                  import('./landlord/create-property/components/create-property-tier/create-property-tier.component').then(
                    (m) => m.CreatePropertyTierComponent,
                  ),
              },
              {
                path: 'guaranteed',
                loadComponent: () =>
                  import('./landlord/create-property/components/create-property-guaranteed/create-property-guaranteed.component').then(
                    (m) => m.CreatePropertyGuaranteedComponent,
                  ),
              },
              {
                path: 'compliance',
                loadComponent: () =>
                  import('./landlord/create-property/components/create-property-compliance/create-property-compliance').then(
                    (m) => m.CreatePropertyCompliance,
                  ),
              },
              {
                path: 'agreement',
                loadComponent: () =>
                  import('./landlord/create-property/components/create-property-agreement/create-property-agreement').then(
                    (m) => m.CreatePropertyAgreement,
                  ),
              },
            ],
          },
        ],
      },
      {
        path: 'listing',
        loadComponent: () =>
          import('./landlord/landlord-listing/landlord-listing.component').then(
            (m) => m.LandlordListingComponent,
          ),
        data: {
          showNavbar: { desktop: true, mobile: false },
          isLandlord: true,
          bottomNav: true,
          reuse: true,
        },
      },
      {
        path: 'offers',
        loadComponent: () =>
          import('./landlord/landlord-all-offers/landlord-all-offers.component').then(
            (m) => m.LandlordAllOffersComponent,
          ),
        providers: [
          provideState(offeringFeature),
          provideEffects(OfferingEffects),
        ],
        data: {
          showNavbar: { desktop: true, mobile: false },
          isLandlord: true,
          bottomNav: true,
          reuse: true,
        },
      },
      {
        path: 'offers/:offerId',
        loadComponent: () =>
          import('./offering/offer-detail/offer-detail-landlord-container.component').then(
            (m) => m.OfferDetailLandlordContainerComponent,
          ),
        providers: [...offeringProviders],
        data: {
          navbar: true,
          bottomNav: false,
          showNavbar: { desktop: true, mobile: false },
        },
      },
      {
        path: 'offers/:offerId/contract/:contractId',
        loadComponent: () =>
          import('./offering/offer-contract-change/offer-contract-change.component').then(
            (m) => m.OfferContractChangeComponent,
          ),
          canDeactivate: [unsavedChangesGuard],
        providers: [...offeringProviders],
        data: {
          navbar: true,
          bottomNav: false,
          showNavbar: { desktop: true, mobile: false },
        },
      },
      {
        path: 'appointments',
        loadComponent: () =>
          import('./landlord/landlord-all-viewings/landlord-all-viewings.component').then(
            (m) => m.LandlordAllViewingsComponent,
          ),
        providers: [
          provideState(viewingFeature),
          provideEffects(ViewingEffects),
        ],
        data: {
          showNavbar: { desktop: true, mobile: false },
          isLandlord: true,
          bottomNav: true,
          reuse: true,
        },
      },
      {
        path: 'appointments/:viewingId',
        loadComponent: () =>
          import('./landlord/landlord-property-detail/landlord-appointment-detail/landlord-appointment-detail').then(
            (m) => m.LandlordAppointmentDetail,
          ),
        providers: [
          provideState(viewingFeature),
          provideEffects(ViewingEffects),
        ],
        data: {
          navbar: true,
          bottomNav: false,
          showNavbar: { desktop: true, mobile: false },
        },
      },
      {
        path: 'property-detail/:id',
        loadComponent: () =>
          import('./landlord/landlord-property-detail/landlord-property-detail.component').then(
            (m) => m.LandlordPropertyDetailComponent,
          ),
        providers: [
          ...landlordPropertyDetailProviders,
          provideState(viewingFeature),
          provideEffects(ViewingEffects),
        ],
        data: {
          showNavbar: { desktop: true, mobile: false },
          bottomNav: false,
        },
        children: [
          { path: '', redirectTo: 'general', pathMatch: 'full' },
          {
            path: 'required-steps',
            loadComponent: () =>
              import('./landlord/landlord-property-detail/landlord-property-required-steps/landlord-property-required-steps.component').then(
                (m) => m.LandlordPropertyRequiredStepsComponent,
              ),
          },
          {
            path: 'service',
            loadComponent: () =>
              import('./landlord/landlord-property-detail/landlord-property-service-summary/landlord-property-service-summary.component').then(
                (m) => m.LandlordPropertyServiceSummaryComponent,
              ),
          },
          {
            path: 'general',
            loadComponent: () =>
              import('./landlord/landlord-property-detail/landlord-property-general/landlord-property-general.component').then(
                (m) => m.LandlordPropertyGeneralComponent,
              ),
          },
          {
            path: 'ownership',
            loadComponent: () =>
              import('./landlord/landlord-property-detail/landlord-property-ownership/landlord-property-ownership').then(
                (m) => m.LandlordPropertyOwnership,
              ),
          },
          {
            path: 'classifications',
            loadComponent: () =>
              import('./landlord/landlord-property-detail/landlord-property-classifications/landlord-property-classifications').then(
                (m) => m.LandlordPropertyClassifications,
              ),
          },
          {
            path: 'amenities',
            loadComponent: () =>
              import('./landlord/landlord-property-detail/landlord-property-amenities/landlord-property-amenities').then(
                (m) => m.LandlordPropertyAmenities,
              ),
          },
          {
            path: 'listing',
            loadComponent: () =>
              import('./landlord/landlord-property-detail/landlord-property-listing/landlord-property-listing').then(
                (m) => m.LandlordPropertyListing,
              ),
          },
          {
            path: 'access',
            loadComponent: () =>
              import('./landlord/landlord-property-detail/landlord-property-access/landlord-property-access').then(
                (m) => m.LandlordPropertyAccess,
              ),
          },
          {
            path: 'gallery',
            loadComponent: () =>
              import('./landlord/landlord-property-detail/landlord-property-gallery/landlord-property-gallery.component').then(
                (m) => m.LandlordPropertyGalleryComponent,
              ),
          },
          {
            path: 'advert',
            loadComponent: () =>
              import('./landlord/landlord-property-detail/landlord-property-detail-advert/landlord-property-detail-advert').then(
                (m) => m.LandlordPropertyDetailAdvert,
              ),
          },
          {
            path: 'documents',
            loadComponent: () =>
              import('./landlord/landlord-property-detail/landlord-property-detail-documents/landlord-property-detail-documents').then(
                (m) => m.LandlordPropertyDetailDocuments,
              ),
          },
          {
            path: 'appointments',
            loadComponent: () =>
              import('./landlord/landlord-property-detail/landlord-property-detail-appointments/landlord-property-detail-appointments').then(
                (m) => m.LandlordPropertyDetailAppointments,
              ),
          },
          {
            path: 'management',
            loadComponent: () =>
              import('./landlord/landlord-property-detail/landlord-property-detail-management/landlord-property-detail-management').then(
                (m) => m.LandlordPropertyDetailManagement,
              ),
          },
          {
            path: 'contract',
            loadComponent: () =>
              import('./landlord/landlord-property-detail/landlord-property-contract/landlord-property-contract.component').then(
                (m) => m.LandlordPropertyContractComponent,
              ),
          },
          {
            path: 'offers',
            loadComponent: () =>
              import('./landlord/landlord-property-offers/landlord-property-offers.component').then(
                (m) => m.LandlordPropertyOffersComponent,
              ),
            data: { navbar: true, bottomNav: false, reuse: true },
            providers: [...offeringProviders],
          },
          {
            path: 'offers/:offerId',
            loadComponent: () =>
              import('./offering/offer-detail/offer-detail-landlord-container.component').then(
                (m) => m.OfferDetailLandlordContainerComponent,
              ),
            data: { navbar: true, bottomNav: false },
            providers: [...offeringProviders],
          },
          {
            path: 'offers/:offerId/contract/:contractId',
            loadComponent: () =>
              import('./offering/offer-contract-change/offer-contract-change.component').then(
                (m) => m.OfferContractChangeComponent,
              ),
              canDeactivate: [unsavedChangesGuard],
            providers: [...offeringProviders],
            data: {
              navbar: true,
              bottomNav: false,
            },
          },
        ],
      },
      {
        path: 'property-detail/:id/contract/:contractId',
        loadComponent: () =>
          import('./landlord/landlord-property-detail/landlord-property-contract-change/landlord-property-contract-change.component').then(
            (m) => m.LandlordPropertyContractChangeComponent,
          ),
          canDeactivate: [unsavedChangesGuard],
        providers: [...landlordPropertyDetailProviders],
        data: {
          showNavbar: { desktop: true, mobile: false },
          bottomNav: false,
        },
      },
      {
        path: 'property-detail/:id/preview',
        loadComponent: () =>
          import('./landlord/landlord-property-detail/property-preview/property-preview.component').then(
            (m) => m.PropertyPreviewComponent,
          ),
        data: { navbar: true, bottomNav: false },
      },
      {
        path: 'wallet',
        providers: [provideState(walletFeature), provideEffects(WalletEffects)],
        children: [
          {
            path: '',
            loadComponent: () =>
              import('./landlord/wallet/wallet.component').then(
                (m) => m.WalletComponent,
              ),
            data: {
              showNavbar: { desktop: true, mobile: false },
              bottomNav: true,
            },
          },
          {
            path: 'transactions',
            canActivate: [permissionGuard],
            loadComponent: () =>
              import('./landlord/wallet/transactions/transactions.component').then(
                (m) => m.TransactionsComponent,
              ),
            data: {
              permission: Permission.LandlordWalletAccess,
              showNavbar: { desktop: true, mobile: false },
              bottomNav: false,
            },
          },
          {
            path: 'withdrawals',
            canActivate: [permissionGuard],
            loadComponent: () =>
              import('./landlord/wallet/withdrawals/withdrawals.component').then(
                (m) => m.WithdrawalsComponent,
              ),
            data: {
              permission: Permission.LandlordWalletAccess,
              showNavbar: { desktop: true, mobile: false },
              bottomNav: false,
            },
          },
        ],
      },
      {
        path: 'tenancies',
        loadComponent: () =>
          import('./landlord/landlord-tenancies/landlord-tenancies.component').then(
            (m) => m.LandlordTenanciesComponent,
          ),
        data: {
          reuse: true,
          showNavbar: { desktop: true, mobile: false },
          bottomNav: true,
        },
        providers: [
          provideState(tenancyFeature),
          provideEffects(TenancyEffects),
        ],
      },
      {
        path: 'tenancies/:id',
        providers: [
          provideState(tenancyFeature),
          provideEffects(TenancyEffects),
        ],
        children: [
          {
            path: '',
            loadComponent: () =>
              import('./tenancy/tenancy-detail/tenancy-detail-landlord-container.component').then(
                (m) => m.TenancyDetailLandlordContainerComponent,
              ),
          },
          {
            path: 'agreement',
            loadComponent: () =>
              import('./tenancy/tenancy-detail/tenancy-agreement/tenancy-agreement.component').then(
                (m) => m.TenancyAgreementComponent,
              ),
          },
        ],
        data: { showNavbar: { desktop: true, mobile: false }, bottomNav: true },
      },
    ],
  },
  {
    path: 'profile',
    loadComponent: () =>
      import('./profile/profile.component').then((m) => m.ProfileComponent),
  },
  {
    path: 'account',
    loadComponent: () =>
      import('./account/account.component').then((m) => m.AccountComponent),
    providers: [provideState(accountFeature), provideEffects(AccountEffects)],
    data: { navbar: true, bottomNav: false },
    children: [
      { path: '', redirectTo: 'personal', pathMatch: 'full' },
      {
        path: 'personal',
        loadComponent: () =>
          import('./account/components/personal-info/personal-info').then(
            (m) => m.PersonalInfo,
          ),
      },
      {
        path: 'verification',
        loadComponent: () =>
          import('./account/components/verifications/verifications').then(
            (m) => m.Verifications,
          ),
        children: [
          {
            path: '',
            pathMatch: 'full',
            loadComponent: () =>
              import('./account/components/verifications-list/verifications-list').then(
                (m) => m.VerificationsList,
              ),
          },
          {
            path: 'answers',
            loadComponent: () =>
              import('./account/components/referencing-answers/referencing-answers').then(
                (m) => m.ReferencingAnswers,
              ),
          },
          {
            path: 'employment-income',
            loadComponent: () =>
              import('./account/components/employment-income-return/employment-income-return').then(
                (m) => m.EmploymentIncomeReturn,
              ),
          },
          {
            path: 'landlord',
            loadComponent: () =>
              import('./account/components/landlord-verification/landlord-verification').then(
                (m) => m.LandlordVerification,
              ),
          },
          {
            path: 'identity-verification',
            loadComponent: () =>
              import('./account/components/idv-details/idv-details').then(
                (m) => m.IdvDetails,
              ),
          },
          {
            path: 'landlord-verification',
            loadComponent: () =>
              import('./account/components/landlord-verification-details/landlord-verification-details').then(
                (m) => m.LandlordVerificationDetails,
              ),
          },
          {
            path: 'right-to-rent',
            loadComponent: () =>
              import('./account/components/right-to-rent-details/right-to-rent-details').then(
                (m) => m.RightToRentDetails,
              ),
          },
          {
            path: 'income-verification',
            loadComponent: () =>
              import('./account/components/income-verification-details/income-verification-details').then(
                (m) => m.IncomeVerificationDetails,
              ),
          },
          {
            path: 'employment-and-income',
            loadComponent: () =>
              import('./account/components/employment-income-details/employment-income-details').then(
                (m) => m.EmploymentIncomeDetails,
              ),
          },
          {
            path: 'savings',
            loadComponent: () =>
              import('./account/components/savings-details/savings-details').then((m) => m.SavingsDetails),
          },
          {
            path: 'credit-check',
            loadComponent: () =>
              import('./account/components/credit-check-details/credit-check-details').then(
                (m) => m.CreditCheckDetails,
              ),
          },
          {
            path: 'employment-reference',
            loadComponent: () =>
              import('./account/components/employment-reference-details/employment-reference-details').then(
                (m) => m.EmploymentReferenceDetails,
              ),
          },
          {
            path: 'landlord-reference',
            loadComponent: () =>
              import('./account/components/landlord-reference-details/landlord-reference-details').then(
                (m) => m.LandlordReferenceDetails,
              ),
          },
        ],
      },
      {
        path: 'notification',
        loadComponent: () =>
          import('./account/components/notifications/notifications').then(
            (m) => m.Notifications,
          ),
        providers: [
          provideState(notificationFeature),
          provideEffects(NotificationEffect),
        ],
      },
      {
        path: 'privacy',
        canActivate: [posthogEnabledGuard],
        loadComponent: () =>
          import('./account/components/privacy/privacy').then((m) => m.Privacy),
      },
      {
        path: 'preferences',
        loadComponent: () =>
          import('./account/components/preferences/preferences').then(
            (m) => m.PreferencesComponent,
          ),
      },
    ],
  },
  {
    path: '**',
    redirectTo: '404',
  },
  {
    path: '404',
    loadComponent: () =>
      import('./not-found/not-found.component').then(
        (m) => m.NotFoundComponent,
      ),
    data: { bottomNav: false },
  },
];
